import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Button, FormLayout, Heading, Input, Select, Switch } from "@/ui";
import { useForm, useValidator } from "@/hooks";
import styles from "./TreezPay.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const TreezPayCredentialForm = ({
    isCreate,
    handleCancel,
    app,
    stores,
    selectCredential,
    setCredentials,
    isUpdating,
    handlerUpdatingOrCreate,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "treezpay_store_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Store provider id is required.",
            },
            {
                field: "treezpay_entity_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Entity Id is required.",
            },
            {
                field: "treezpay_username",
                method: "isEmpty",
                validWhen: false,
                message: "This Username is required.",
            },
            {
                field: "treezpay_password",
                method: "isEmpty",
                validWhen: false,
                message: "This Password is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const storeOptions = useMemo(
        () =>
            (stores || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [stores],
    );

    useEffect(() => {
        if (!isCreate) {
            const objSelect = app?.credentials[selectCredential];
            setInputs({
                treezpay_sandbox: objSelect?.treezpay_sandbox ?? false,
                treezpay_store_provider_id:
                    objSelect?.treezpay_store_provider_id,
                treezpay_entity_id: objSelect?.treezpay_entity_id,
                treezpay_username: objSelect?.treezpay_username,
                treezpay_password: objSelect?.treezpay_password,
            });
        }
    }, []);

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            let accounts = _.cloneDeep(app.credentials ?? []);

            if (isCreate) {
                accounts.push({
                    treezpay_sandbox: inputs?.treezpay_sandbox ?? false,
                    treezpay_store_provider_id:
                        inputs?.treezpay_store_provider_id,
                    treezpay_entity_id: inputs?.treezpay_entity_id,
                    treezpay_username: inputs?.treezpay_username,
                    treezpay_password: inputs?.treezpay_password,
                });
            } else {
                accounts[selectCredential] = inputs;
            }
            setCredentials(accounts);
            handlerUpdatingOrCreate(accounts);
        }
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleStoreProviderChange = value => {
        const store = (stores || []).find(s => String(s.value) === value);
        setInputs({
            ...inputs,
            treezpay_store_provider_id: store ? store.value : value,
        });
    };

    return (
        <div className={styles.credentialForm}>
            <Heading level={5}>
                {isCreate ? "Add Credential" : "Update Credential"}
            </Heading>

            <FormLayout>
                <FormLayout.Section title="Credential Informations">
                    <Switch
                        checked={Boolean(inputs.treezpay_sandbox)}
                        label="Sandbox"
                        name="treezpay_sandbox"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Select
                            error={fieldError(
                                validator,
                                "treezpay_store_provider_id",
                            )}
                            fullWidth
                            label="Store Provider Id"
                            onValueChange={handleStoreProviderChange}
                            options={storeOptions}
                            value={
                                inputs.treezpay_store_provider_id != null
                                    ? String(inputs.treezpay_store_provider_id)
                                    : undefined
                            }
                        />
                        <Input
                            error={fieldError(validator, "treezpay_entity_id")}
                            fullWidth
                            label="Entity Id"
                            name="treezpay_entity_id"
                            onChange={inputChange}
                            placeholder="Entity Id"
                            value={inputs.treezpay_entity_id || ""}
                        />
                        <Input
                            error={fieldError(validator, "treezpay_username")}
                            fullWidth
                            label="Username"
                            name="treezpay_username"
                            onChange={inputChange}
                            placeholder="Username"
                            value={inputs.treezpay_username || ""}
                        />
                        <Input
                            error={fieldError(validator, "treezpay_password")}
                            fullWidth
                            label="Password"
                            name="treezpay_password"
                            onChange={inputChange}
                            placeholder="Password"
                            type="password"
                            value={inputs.treezpay_password || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>
            </FormLayout>

            <div className={styles.credentialActions}>
                <Button
                    disabled={isUpdating}
                    onClick={handleClose}
                    type="button"
                    variant="secondary"
                >
                    Cancel
                </Button>
                <Button
                    loading={isUpdating}
                    onClick={createOrUpdate}
                    type="button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default TreezPayCredentialForm;
